import { z } from 'zod';
export const Uuid = z.uuid();
export const AssetRef = z
    .object({
    assetId: Uuid,
    altOverride: z.string().min(1).optional(),
})
    .strict();
export const PageRoute = z.enum([
    '/',
    '/about',
    '/portfolio',
    '/pricing',
    '/journal',
    '/gift',
    '/proposals',
    '/weddings',
]);
const ExternalHttpsHref = z
    .string()
    .url()
    .refine((value) => URL.canParse(value) && new URL(value).protocol === 'https:', 'Expected an HTTPS URL');
export const LinkHref = z.union([PageRoute, ExternalHttpsHref]);
export const Action = z
    .object({
    label: z.string().min(1),
    href: LinkHref,
    style: z.enum(['Primary', 'Secondary']),
})
    .strict();
const TextNode = z
    .object({
    type: z.literal('text'),
    text: z.string(),
    marks: z.array(z.enum(['strong', 'emphasis'])).optional(),
})
    .strict();
const LinkTextNode = z
    .object({
    type: z.literal('text'),
    text: z.string(),
})
    .strict();
const LinkNode = z
    .object({
    type: z.literal('link'),
    href: LinkHref,
    children: z.array(LinkTextNode).min(1),
})
    .strict();
export const InlineNode = z.discriminatedUnion('type', [TextNode, LinkNode]);
export const RichTextNode = z.discriminatedUnion('type', [
    z
        .object({
        type: z.enum(['paragraph', 'h3']),
        children: z.array(InlineNode).min(1),
    })
        .strict(),
    z
        .object({
        type: z.literal('bulletList'),
        items: z.array(z.array(InlineNode).min(1)).min(1),
    })
        .strict(),
]);
export const RichText = z
    .object({
    type: z.literal('document'),
    children: z.array(RichTextNode),
})
    .strict();
export const GalleryLayout = z.enum(['EditorialGrid', 'Filmstrip', 'Masonry']);
export const HeadingParts = z
    .object({
    before: z.string().min(1),
    accent: z.string().min(1).optional(),
    after: z.string().min(1).optional(),
})
    .strict();
export const SeoFields = z
    .object({
    title: z.string().min(1),
    description: z.string().min(1),
    noindex: z.boolean(),
})
    .strict();
const InternalGalleryDestination = z.union([
    z.literal('/portfolio'),
    z.string().regex(/^\/portfolio\/[a-z0-9]+(?:-[a-z0-9]+)*(?:\?[^#\s]*)?$/),
]);
const ExternalGalleryDestination = z
    .string()
    .url()
    .refine((value) => URL.canParse(value) && new URL(value).protocol === 'https:', 'Expected an HTTPS URL');
const GalleryDestination = z.union([InternalGalleryDestination, ExternalGalleryDestination]);
export const GalleryItem = z
    .object({
    key: z.string().min(1).optional(),
    category: z.string().min(1).optional(),
    destination: GalleryDestination.optional(),
    external: z.boolean().optional(),
    showOnHome: z.boolean().optional(),
    media: AssetRef,
    caption: z.string().min(1).optional(),
})
    .strict();
export const ProcessItem = z
    .object({
    key: z.string().min(1),
    eyebrow: z.string().min(1).optional(),
    title: z.string().min(1),
    body: RichText,
})
    .strict();
export const FaqItem = z
    .object({
    key: z.string().min(1),
    question: z.string().min(1),
    answer: RichText,
})
    .strict();
export const FeatureItem = z
    .object({
    key: z.string().min(1),
    title: z.string().min(1),
    body: RichText.optional(),
    media: AssetRef.optional(),
})
    .strict();
export const HeroImageCrop = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
})
    .strict()
    .refine((crop) => crop.x + crop.width <= 1 && crop.y + crop.height <= 1, {
    message: 'Crop must stay within the source image',
});
const HeroImageFrame = z.object({ media: AssetRef, crop: HeroImageCrop }).strict();
export const HeroImageComposition = z
    .object({
    source: AssetRef.optional(),
    desktop: HeroImageFrame,
    mobile: HeroImageFrame,
})
    .strict();
export const HeroPayload = z
    .object({
    eyebrow: z.string().min(1).optional(),
    title: z.string().min(1),
    accent: z.string().min(1).optional(),
    lead: z.string().min(1).optional(),
    meta: z.array(z.string().min(1)).max(4).optional(),
    subtitle: RichText.optional(),
    action: Action.optional(),
    media: AssetRef.optional(),
    imageComposition: HeroImageComposition.optional(),
    slides: z
        .array(z
        .object({
        media: AssetRef,
        label: z.string().min(1).optional(),
        shootId: Uuid.optional(),
    })
        .strict())
        .min(1)
        .optional(),
    inquiry: z
        .object({
        heading: z.string().min(1),
        emphasis: z.string().min(1),
        intro: z.string().min(1),
        messageLabel: z.string().min(1),
        recipientLabel: z.string().min(1),
        submitLabel: z.string().min(1),
        successMessage: z.string().min(1),
    })
        .strict()
        .optional(),
    certificate: z
        .object({
        label: z.string().min(1),
        titleLines: z.array(z.string().min(1)).min(1).max(4),
        metaLines: z.array(z.string().min(1)).min(1).max(4),
        signature: z.string().min(1),
    })
        .strict()
        .optional(),
    feature: z
        .object({
        media: AssetRef,
        label: z.string().min(1),
        statement: z.string().min(1),
        statementBody: RichText.optional(),
        note: z.string().min(1).optional(),
        noteBody: RichText.optional(),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if ((value.media === undefined) === (value.slides === undefined)) {
        for (const path of [['media'], ['slides']]) {
            ctx.addIssue({
                code: 'custom',
                path,
                message: 'Hero requires exactly one of media or slides',
            });
        }
    }
});
export const EditorialTextPayload = z
    .object({
    eyebrow: z.string().min(1).optional(),
    heading: z.string().min(1).optional(),
    accent: z.string().min(1).optional(),
    body: RichText,
    aside: RichText.optional(),
    media: z.array(AssetRef).max(10).optional(),
    paragraphs: z.array(z.string().min(1)).max(6).optional(),
    signature: z.string().min(1).optional(),
    feature: z
        .object({
        media: AssetRef,
        label: z.string().min(1),
        statement: z.string().min(1),
        statementBody: RichText.optional(),
        note: z.string().min(1).optional(),
        noteBody: RichText.optional(),
    })
        .strict()
        .optional(),
    gallery: z
        .object({
        eyebrow: z.string().min(1).optional(),
        heading: z.string().min(1).optional(),
        accent: z.string().min(1).optional(),
        body: RichText.optional(),
        items: z.array(GalleryItem).min(1).max(12),
        actionLabel: z.string().min(1).optional(),
    })
        .strict()
        .optional(),
    facts: z
        .object({
        eyebrow: z.string().min(1).optional(),
        heading: HeadingParts,
        items: z
            .array(z
            .object({
            key: z.string().min(1),
            label: z.string().min(1),
            text: z.string().min(1),
            accent: z.string().min(1).optional(),
        })
            .strict())
            .min(1)
            .max(8),
    })
        .strict()
        .optional(),
    press: z
        .object({
        eyebrow: z.string().min(1),
        heading: HeadingParts,
        items: z
            .array(z
            .object({
            key: z.string().min(1),
            title: z.string().min(1),
            accent: z.string().min(1).optional(),
            meta: z.string().min(1),
            year: z.string().min(1).optional(),
            href: z.string().url().optional(),
        })
            .strict())
            .max(12),
    })
        .strict()
        .optional(),
    education: z
        .object({
        eyebrow: z.string().min(1),
        heading: HeadingParts,
        note: z.string().min(1).optional(),
        items: z
            .array(z
            .object({
            key: z.string().min(1),
            title: z.string().min(1),
            accent: z.string().min(1).optional(),
            meta: z.string().min(1),
            year: z.string().min(1).optional(),
        })
            .strict())
            .max(12),
    })
        .strict()
        .optional(),
})
    .strict();
export const FeaturePayload = z
    .object({
    variant: z.enum(['ImageText', 'Facts', 'Testimonial', 'Collection']),
    heading: z.string().min(1),
    body: RichText,
    media: AssetRef.optional(),
    items: z.array(FeatureItem).min(1).optional(),
    action: Action.optional(),
})
    .strict();
export const GalleryPayload = z
    .object({
    heading: z.string().min(1).optional(),
    body: RichText.optional(),
    layout: GalleryLayout,
    items: z.array(GalleryItem).min(1),
    categories: z
        .array(z
        .object({
        key: z.string().min(1),
        slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
        title: z.string().min(1),
        subtitle: z.string().min(1),
        hideFromHome: z.boolean(),
        visible: z.boolean(),
        seo: SeoFields,
    })
        .strict())
        .max(12)
        .optional(),
    actionLabel: z.string().min(1).optional(),
})
    .strict();
const SplitPanel = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('Text'), value: RichText }).strict(),
    z.object({ kind: z.literal('Media'), value: AssetRef }).strict(),
]);
export const SplitPayload = z
    .object({ left: SplitPanel, right: SplitPanel })
    .strict()
    .superRefine((value, ctx) => {
    if (value.left.kind !== 'Text' && value.right.kind !== 'Text') {
        for (const path of [['left'], ['right']]) {
            ctx.addIssue({ code: 'custom', path, message: 'Split requires a text panel' });
        }
    }
});
export const PricingPayload = z
    .object({
    catalogKey: z.literal('main'),
    heading: z.string().min(1).optional(),
    travelNote: z.string().min(1).optional(),
    ownershipVersion: z.literal(1).optional(),
    extrasHeading: z.string().min(1).optional(),
    extras: z
        .array(z
        .object({
        key: z.string().min(1),
        label: z.string().min(1),
        priceLabel: z.string().min(1),
    })
        .strict())
        .optional(),
    gift: z
        .object({
        heading: z.string().min(1),
        body: RichText,
        actionLabel: z.string().min(1),
    })
        .strict()
        .optional(),
})
    .strict();
const ProcessDetail = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    text: z.string().min(1),
    accent: z.string().min(1).optional(),
})
    .strict();
const ProcessChapter = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    heading: HeadingParts,
    items: z.array(ProcessItem).min(1).max(8),
})
    .strict();
const ProcessReview = z
    .object({
    key: z.string().min(1),
    quote: z.string().min(1),
    attribution: z.string().min(1),
    approvedForWebsite: z.boolean().optional(),
})
    .strict();
const ProcessEditorialFields = {
    eyebrow: z.string().min(1).optional(),
    accent: z.string().min(1).optional(),
    body: RichText.optional(),
    actionLabel: z.string().min(1).optional(),
    details: z.array(ProcessDetail).max(6).optional(),
    chapters: z.array(ProcessChapter).max(4).optional(),
    reviews: z.array(ProcessReview).max(8).optional(),
    packagesHeading: HeadingParts.optional(),
    reviewsHeading: HeadingParts.optional(),
    statement: HeadingParts.extend({
        secondAccent: z.string().min(1).optional(),
        afterSecond: z.string().min(1).optional(),
    }).optional(),
};
export const ProcessPayload = z.discriminatedUnion('source', [
    z
        .object({
        source: z.literal('Pricing'),
        heading: z.string().min(1).optional(),
        ...ProcessEditorialFields,
    })
        .strict(),
    z
        .object({
        source: z.literal('Inline'),
        heading: z.string().min(1).optional(),
        items: z.array(ProcessItem).min(1),
        ...ProcessEditorialFields,
    })
        .strict(),
]);
export const FaqPayload = z.discriminatedUnion('source', [
    z
        .object({
        source: z.literal('Pricing'),
        eyebrow: z.string().min(1).optional(),
        heading: z.string().min(1).optional(),
        accent: z.string().min(1).optional(),
    })
        .strict(),
    z
        .object({
        source: z.literal('Inline'),
        eyebrow: z.string().min(1).optional(),
        heading: z.string().min(1).optional(),
        accent: z.string().min(1).optional(),
        items: z.array(FaqItem).min(1),
    })
        .strict(),
]);
export const JournalGridPayload = z
    .object({
    category: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(24).optional(),
})
    .strict();
export const CtaPayload = z.preprocess((value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return value;
    // Old immutable releases may contain this retired field. Read them without
    // returning it to editors or persisting it in a new draft/release.
    const { eyebrow: _retiredEyebrow, ...payload } = value;
    return payload;
}, z
    .object({
    heading: z.string().min(1),
    accent: z.string().min(1).optional(),
    body: RichText.optional(),
    action: Action,
    backgroundMedia: AssetRef.optional(),
    visibleSocialLinks: z.array(ExternalHttpsHref).optional(),
})
    .strict());
const pageBlock = (type, payload) => z
    .object({
    id: Uuid,
    type: z.literal(type),
    enabled: z.boolean(),
    position: z.number().int().nonnegative(),
    payload,
})
    .strict();
export const PageBlock = z.discriminatedUnion('type', [
    pageBlock('Hero', HeroPayload),
    pageBlock('EditorialText', EditorialTextPayload),
    pageBlock('Feature', FeaturePayload),
    pageBlock('Gallery', GalleryPayload),
    pageBlock('Split', SplitPayload),
    pageBlock('Pricing', PricingPayload),
    pageBlock('Process', ProcessPayload),
    pageBlock('FAQ', FaqPayload),
    pageBlock('JournalGrid', JournalGridPayload),
    pageBlock('CTA', CtaPayload),
]);
export const PricingPageContentV1 = z.array(PageBlock).superRefine((blocks, ctx) => {
    const hero = blocks.find((block) => block.type === 'Hero');
    const pricing = blocks.find((block) => block.type === 'Pricing');
    const process = blocks.find((block) => block.type === 'Process');
    const faq = blocks.find((block) => block.type === 'FAQ');
    const issue = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    if (!hero || hero.type !== 'Hero') {
        issue(['blocks'], 'Pricing page requires a Hero block');
    }
    else {
        if (!hero.payload.subtitle)
            issue(['Hero', 'subtitle'], 'Pricing introduction is required');
        if (hero.payload.meta === undefined) {
            issue(['Hero', 'meta'], 'Pricing benefits must be explicitly configured');
        }
    }
    if (!pricing || pricing.type !== 'Pricing') {
        issue(['blocks'], 'Pricing page requires a Pricing block');
    }
    else {
        if (pricing.payload.ownershipVersion !== 1) {
            issue(['Pricing', 'ownershipVersion'], 'Pricing page ownership version must be 1');
        }
        if (!pricing.payload.extrasHeading) {
            issue(['Pricing', 'extrasHeading'], 'Extras heading is required');
        }
        if (pricing.payload.extras === undefined) {
            issue(['Pricing', 'extras'], 'Extras must be explicitly configured');
        }
        if (!pricing.payload.gift) {
            issue(['Pricing', 'gift'], 'Gift certificate content is required');
        }
    }
    if (!process || process.type !== 'Process' || process.payload.source !== 'Inline') {
        issue(['Process', 'source'], 'Pricing process must use inline page content');
    }
    else {
        if (!process.payload.eyebrow)
            issue(['Process', 'eyebrow'], 'Process label is required');
        if (!process.payload.heading)
            issue(['Process', 'heading'], 'Process heading is required');
        if (!process.payload.actionLabel) {
            issue(['Process', 'actionLabel'], 'Process action label is required');
        }
    }
    if (!faq || faq.type !== 'FAQ' || faq.payload.source !== 'Inline') {
        issue(['FAQ', 'source'], 'Pricing FAQ must use inline page content');
    }
    else {
        if (!faq.payload.eyebrow)
            issue(['FAQ', 'eyebrow'], 'FAQ label is required');
        if (!faq.payload.heading)
            issue(['FAQ', 'heading'], 'FAQ heading is required');
    }
});
